<template>
  <b-container class="mt-4">
    <b-row>
      <b-col class="header">{{ $t('common.favourites') }}</b-col>
    </b-row>
    <b-row class="mt-4">
      <b-col>
        <vue-headful :title="this.setPageTitle('followed')" />
        <!-- Catalog-native filter panel — only the filters flagged is_followed for this
             asset class (curated 4-filter set, not the full search sidebar). Local
             component state, no separate Apply button (debounced auto-search on change). -->
        <b-row>
          <b-col v-for="filter in followedFilters" :key="filter.filterKey" cols="3">
            <label v-if="filter.filterType !== 'RangeDate'" class="fieldLabel">{{ filterLabel(filter.filterKey) }}</label>
            <filter-field
              :filter="filter"
              :value="filterValue(filter.filterKey)"
              :label="filter.filterType === 'RangeDate' ? filterLabel(filter.filterKey) : null"
              :show-count="false"
              @input="(value) => onFilterInput(filter.filterKey, value)"
            ></filter-field>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col cols="3" offset="9">
            <b-button size="sm" class="w-100" variant="primary" @click="resetFilters">
              {{ $t('search.label.removeFilters') }}
            </b-button>
          </b-col>
        </b-row>
        <template v-if="isLoading">
          <b-row>
            <b-col>
              <div class="d-flex text-center loading-container">
                <div class="w-100">
                  <custom-spinner :message="$t('common.itemsFound')"></custom-spinner>
                </div>
              </div>
            </b-col>
          </b-row>
        </template>
        <div v-else>
          <b-row v-if="!isSearchResultsFound">
            <b-col>
              <no-content
                :icon-name="'exclamation-circle'"
                :icon-class="['mb-3', 'fa-3x']"
                :text="$t('common.noItemsFound')"
              ></no-content>
            </b-col>
          </b-row>
          <b-row v-else class="mt-5">
            <b-col cols="12" class="results-container">
              <results-table
                :paginator="paginator"
                :search-result="searchResult"
                :shown-fields="shownFields"
                :sorting-changed="sortingChanged"
                :is-downloading="isDownloading"
                @on-change-page-size="onChangePageSize"
                @on-change-page-index="onChangePageIndex"
                @export-requested="exportData"
              />
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { downloadFile } from '../utils/fileManager'
import { hasExportPermission } from '../utils/exportPermission'
import { debounce } from '../utils/limiters'
import ResultsTable from '../components/Search/ResultsTable'
import NoContent from '../components/Lib/NoContent'
import CustomSpinner from '../components/Lib/CustomSpinner'
import FilterField from '../components/Search/FilterField'
import { buildShownFields, flattenResultRow } from '../utils/searchResultsFieldFormat'
import { assetClassStorageKey } from '../utils/assetClassState'

// Columns come from the Search Results Catalog (configs_result_fields),
// but from the page_key=Followed row set — independent from the search page's page_key=Search rows,
// resolved by the dedicated `POST /followed/results` endpoint. No tab filtering needed here: the
// BE already returns exactly the intended columns for this page, in order — see
// buildShownFields()'s omitted-tabKey branch. Asset-class-correct automatically.

// Selected filters persist across reloads/tabs, same manual
// localStorage pattern as search.js's FILTERS_STORAGE_KEY. Asset-class-scoped —
// switching asset class triggers a full reload, so re-evaluating this at module load picks up the
// new slug correctly.
const FILTERS_STORAGE_KEY = assetClassStorageKey('followedFilters')

function loadPersistedFilters() {
  try {
    const parsed = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY))
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persistFilters(filters) {
  localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters))
}

export default {
  name: 'Followed',
  components: { ResultsTable, NoContent, CustomSpinner, FilterField },
  data() {
    return {
      followedFilters: [],
      activeFilters: loadPersistedFilters(),
      resultColumns: [],
      paginator: {
        pageIndex: 1,
        totalItems: 0,
        pageSize: 100,
      },
      searchResult: {
        sortBy: '-referenceDate',
        results: [],
      },
      isLoading: true,
      loaderColor: 'blue',
      fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
      isDownloading: false,
    }
  },
  computed: {
    // "isFollowed" (follow-star column) isn't a catalog field — see FollowedResultsEndpoint, it's
    // added to every row explicitly, same exception as on /search-new's grid.
    shownFields: function () {
      return [{ key: 'isFollowed', label: '', sortable: false }, ...buildShownFields(this, this.resultColumns)]
    },
    canExport: function () {
      return hasExportPermission(this.$store.getters.getPermissions)
    },
    isSearchResultsFound() {
      return Array.isArray(this.searchResult.results) && this.searchResult.results.length > 0
    },
  },
  watch: {
    'searchResult.sortBy': function () {
      this.searchRequest()
    },
  },
  methods: {
    filterLabel(filterKey) {
      const key = 'search.filters.' + filterKey
      return this.$te(key) ? this.$t(key) : filterKey
    },
    filterValue(filterKey) {
      const entry = this.activeFilters.find((f) => f.key === filterKey)
      return entry ? entry.value : null
    },
    onFilterInput(filterKey, value) {
      const others = this.activeFilters.filter((f) => f.key !== filterKey)
      const isEmptyRange =
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.values(value).every((v) => v === null || v === undefined)
      const isEmpty = value === null || value === undefined || (Array.isArray(value) && value.length === 0) || isEmptyRange
      this.activeFilters = isEmpty ? others : [...others, { key: filterKey, value }]
      persistFilters(this.activeFilters)
      this.debouncedSearch()
    },
    resetFilters() {
      this.activeFilters = []
      persistFilters(this.activeFilters)
      this.paginator.pageIndex = 1
      this.searchRequest()
    },
    searchRequest() {
      this.isLoading = true
      // followedOnly is not sent: the endpoint always restricts to the current user's followed
      // products server-side, the caller has no say in it (see FollowedResultsEndpoint).
      this.post('/api/followed/results', {
        filters: this.activeFilters,
        sort: this.searchResult.sortBy,
        page: this.paginator.pageIndex,
        size: this.paginator.pageSize,
      })
        .then((resp) => {
          if (resp && resp.rows != null) {
            this.searchResult.results = resp.rows.map(flattenResultRow)
            this.resultColumns = resp.columns || []
            this.paginator.totalItems = resp.paging.totalItems
            this.paginator.pageSize = resp.paging.pageSize
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    sortingChanged(ctx) {
      this.searchResult.sortBy = (ctx.sortDesc ? '-' : '') + ctx.sortBy
    },
    onChangePageSize(value) {
      this.paginator.pageSize = value
      this.paginator.pageIndex = 1
      this.searchRequest()
    },
    onChangePageIndex(value) {
      this.paginator.pageIndex = value
      this.searchRequest()
    },
    exportData(includeSids) {
      if (!this.canExport) return

      this.isDownloading = true
      const isInvestmentCertificates = this.$store.getters.getSelectedAssetClass === 'investment-certificates'
      const fileName = includeSids
        ? isInvestmentCertificates
          ? 'OpenKid-Export-InvestmentCertificates.xlsx'
          : 'OpenKid-Export-SID.xlsx'
        : 'OpenKid-Export-Products.xlsx'

      const endpoint = includeSids ? '/api/export/sids' : '/api/export/products'
      // Catalog-native (both /api/export/products and /api/export/sids now take productSearchFilters
      // directly — see CHARTS_CATALOG_DESIGN.md item "2b"). followedOnly:true mirrors the same
      // conditional flag SearchProductsEndpoint/SearchResultsEndpoint already expose, restricting
      // the export to this user's followed products server-side.
      downloadFile(
        this,
        'post',
        endpoint,
        { productSearchFilters: this.activeFilters, followedOnly: true },
        this.fileType,
        'isDownloading',
        fileName,
        this.$t('search.label.exportResultsEmpty')
      )
    },
  },
  mounted() {
    this.debouncedSearch = debounce(this.searchRequest, 500)
    this.get('/api/followed/filters-catalog').then((catalog) => {
      this.followedFilters = [...((catalog && catalog.groups) || []).flatMap((g) => g.filters), ...((catalog && catalog.ungrouped) || [])]
    })
    this.searchRequest()
  },
}
</script>

<style lang="scss" scoped>
.header {
  font-size: 1.5rem;
}

.fieldLabel {
  margin-right: 5px;
}

.loading-container {
  padding: 4rem 0;
}

.results-container {
  min-height: 150px;
}
</style>
